import { useEffect, useRef, useState } from "react";
import InteractiveAurora from "./InteractiveAurora";
import HeroContent from "./HeroContent";
import HeroMagnifier, { type HeroMagnifierHandle } from "./HeroMagnifier";

const LENS_RADIUS = 110;

export default function Hero() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const baseRef = useRef<HTMLDivElement | null>(null);
  const magnifierRef = useRef<HeroMagnifierHandle | null>(null);
  const targetRef = useRef({ x: -9999, y: -9999 });
  const currentRef = useRef({ x: -9999, y: -9999 });
  const visibleRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const [everInside, setEverInside] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const rect = () => el.getBoundingClientRect();

    const onMove = (e: MouseEvent) => {
      const r = rect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      const inside = x >= 0 && y >= 0 && x <= r.width && y <= r.height;
      visibleRef.current = inside;
      if (inside) {
        targetRef.current = { x, y };
        if (!everInside) setEverInside(true);
      }
    };

    const tick = () => {
      const t = targetRef.current;
      const c = currentRef.current;
      c.x += (t.x - c.x) * 0.35;
      c.y += (t.y - c.y) * 0.35;

      const visible = visibleRef.current;
      // Mask the base content: cut a transparent hole where the lens sits so
      // only the magnified copy shows through inside the circle.
      if (baseRef.current) {
        if (visible) {
          const mask = `radial-gradient(circle ${LENS_RADIUS}px at ${c.x}px ${c.y}px, transparent 99%, black 100%)`;
          baseRef.current.style.maskImage = mask;
          (baseRef.current.style as CSSStyleDeclaration & { webkitMaskImage?: string }).webkitMaskImage = mask;
        } else {
          baseRef.current.style.maskImage = "";
          (baseRef.current.style as CSSStyleDeclaration & { webkitMaskImage?: string }).webkitMaskImage = "";
        }
      }
      magnifierRef.current?.update(c.x, c.y, visible);
      rafRef.current = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [everInside]);

  return (
    <section
      ref={sectionRef}
      id="overview"
      className="relative w-full min-h-screen flex items-center justify-center overflow-hidden bg-background-50"
    >
      {/* Ambient aurora background */}
      <InteractiveAurora />

      {/* Base hero content — a mask cuts a hole under the lens */}
      <div ref={baseRef} className="absolute inset-0 flex items-center justify-center">
        <HeroContent />
      </div>

      {/* Magnifier lens shows the same content, magnified, inside a circle */}
      <HeroMagnifier ref={magnifierRef} lensRadius={LENS_RADIUS} />
    </section>
  );
}